import { useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import { Play, Volume2, Pause } from 'lucide-react'
import { apiClient } from '@/services/apiClient'
import { LocalizedString, getLocalizedValue } from '@sakeena/shared'
import { useTranslation } from 'react-i18next'
import { getLangFromPathname } from '@/utils/localeFromUrl'
import { cn } from '@/utils/cn'

interface SectionCard {
  id?: string
  Id?: string
  title?: LocalizedString | string
  Title?: LocalizedString | string
  body?: LocalizedString | string
  Body?: LocalizedString | string
  imageUrl?: string
  ImageUrl?: string
  audioUrl?: string
  AudioUrl?: string
  audioDurationSeconds?: number
  AudioDurationSeconds?: number
  ctaText?: LocalizedString | string
  CtaText?: LocalizedString | string
  ctaLink?: string
  CtaLink?: string
  order?: number
  Order?: number
  isActive?: boolean
  IsActive?: boolean
}

// Helper to normalize card data (handle both camelCase and PascalCase)
const normalizeCard = (card: any): SectionCard => ({
  id: card.id || card.Id || '',
  title: card.title || card.Title || '',
  body: card.body || card.Body || '',
  imageUrl: card.imageUrl || card.ImageUrl,
  audioUrl: card.audioUrl || card.AudioUrl,
  audioDurationSeconds: card.audioDurationSeconds ?? card.AudioDurationSeconds ?? null,
  ctaText: card.ctaText || card.CtaText || '',
  ctaLink: card.ctaLink || card.CtaLink || '',
  order: card.order ?? card.Order ?? 0,
  isActive: card.isActive ?? card.IsActive ?? true,
})

// Helper function to format duration
const formatDuration = (seconds?: number): string => {
  if (!seconds) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const LG_BREAKPOINT = 1024
function useIsSingleColumn() {
  const [single, setSingle] = useState(true)
  useEffect(() => {
    const check = () => setSingle(window.innerWidth < LG_BREAKPOINT)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  return single
}

export default function IntroCards() {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const pathLang = getLangFromPathname(location.pathname)
  const currentLang = i18n.language || 'ar'
  const [cards, setCards] = useState<SectionCard[]>([])
  const [loading, setLoading] = useState(true)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement }>({})
  const isSingleColumn = useIsSingleColumn()

  // Fallback data in case API fails
  const fallbackItems: SectionCard[] = [
    {
      id: 'definition',
      title: t('islamDefinition.title', 'تعريف بالإسلام'),
      body: t('islamDefinition.subtitle', 'الإسلام دين التوحيد، يشمل أعمال الخير ويبعد عن الشر، ويتضمن التسليم لله تعالى والالتزام بأوامره...'),
      ctaText: t('home.readArticle', 'قراءة المقال'),
      ctaLink: '#',
      order: 0,
      isActive: true
    },
    {
      id: 'why',
      title: t('home.topicGrid.why.title', 'لماذا خلقنا؟'),
      body: t('home.topicGrid.why.desc', 'ويشترط لوجوب الجهاد سبعة شروط؛ الإسلام، والبلوغ، والعقل، والحرية، والذكورية، السلامة...'),
      ctaText: t('home.readArticle', 'قراءة المقال'),
      ctaLink: '#',
      order: 1,
      isActive: true
    }
  ]

  useEffect(() => {
    const loadCards = async () => {
      try {
        setLoading(true)
        const response = await apiClient.get<any[]>('/homepage/section-cards')
        const normalizedCards = response.data.map(normalizeCard)
        const sortedCards = normalizedCards
          .filter(card => card.isActive)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
        // API returns only cards with content in the selected language; show them as-is. Use fallback only on API error.
        setCards(sortedCards)
      } catch (error) {
        console.error('Failed to load section cards:', error)
        setCards(fallbackItems)
      } finally {
        setLoading(false)
      }
    }

    loadCards()
  }, [t, i18n.language])

  const handlePlayPause = (cardId: string | undefined, audioUrl?: string) => {
    if (!audioUrl || !cardId) return

    const audio = audioRefs.current[cardId]
    if (!audio) {
      const newAudio = new Audio(audioUrl)
      audioRefs.current[cardId] = newAudio
      newAudio.play()
      setPlayingId(cardId)
      
      newAudio.addEventListener('ended', () => {
        setPlayingId(null)
      })
      return
    }

    if (playingId === cardId) {
      audio.pause()
      setPlayingId(null)
    } else {
      Object.values(audioRefs.current).forEach(a => a.pause())
      audio.play()
      setPlayingId(cardId)
    }
  }

  if (loading) {
    return (
      <section className="py-12 md:py-16 bg-gray-100 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-slate-600">{t('home.introCards.loading', 'جاري التحميل...')}</div>
        </div>
      </section>
    )
  }

  if (cards.length === 0) {
    return null
  }

  return (
    <section className="py-12 md:py-16 bg-gray-100 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-stretch">
          {cards.map((card, index) => {
            // على الويب (عمودين): صف وعمود. على الموبايل (عمود واحد): كل كارت صف لوحده
            const cols = 2
            const row = Math.floor(index / cols)
            const col = index % cols
            // على الموبايل (عمود واحد): تناوب واحدة وواحدة (فردي مينت، زوجي أبيض). على الويب (عمودين): شبكة ٢×٢
            const isMint = isSingleColumn ? (index % 2 === 0) : ((row + col) % 2 === 0)
            const isPlaying = playingId === card.id

            return (
              <Card
                key={card.id}
                className={`h-full min-h-[280px] md:min-h-[320px] flex flex-col p-6 md:p-8 rounded-2xl md:rounded-3xl border-0 shadow-[0_4px_20px_rgba(0,0,0,0.08),0_0_1px_rgba(0,0,0,0.06)] ${
                  isMint ? 'bg-emerald-50/90 dark:bg-emerald-900/20' : 'bg-white dark:bg-gray-800'
                }`}
              >
                <div className="flex flex-col h-full min-h-0 text-center">
                  <h3 className="text-2xl md:text-3xl font-bold text-black dark:text-slate-100 mb-4 leading-snug shrink-0">
                    {getLocalizedValue(card.title, currentLang)}
                  </h3>

                  <div className="text-slate-600 dark:text-slate-300 mb-6 leading-relaxed text-sm md:text-base overflow-hidden flex-1 flex flex-col min-h-0">
                    <div
                      className="prose prose-sm dark:prose-invert max-w-none text-inherit prose-p:my-0 prose-p:mb-0 [&>*]:my-0 [&>*]:mb-0 min-h-[6.5rem] max-h-[6.5rem] md:min-h-[7rem] md:max-h-[7rem] overflow-hidden"
                      dangerouslySetInnerHTML={{
                        __html: getLocalizedValue(card.body, currentLang) || '',
                      }}
                    />
                  </div>

                  {/* تناوب لون الزر حسب الكارت (مينت = زر فاتح، أبيض = زر أخضر) — نفس السلوك في الموبايل والويب */}
                  <div className="mt-auto flex flex-row flex-wrap items-center justify-center gap-3 w-full flex-shrink-0">
                    <Button
                      variant={isMint ? 'ghost' : 'primary'}
                      className={cn(
                        'shrink-0 px-6 py-2.5 rounded-xl text-sm md:text-base border-0',
                        isMint
                          ? '!bg-white dark:!bg-gray-800 !text-black dark:!text-slate-900 hover:!bg-emerald-50 dark:hover:!bg-emerald-900/20'
                          : '!bg-emerald-600 hover:!bg-emerald-700 !text-white'
                      )}
                      onClick={() => {
                        const cardId = card.id || ''
                        if (cardId) {
                          navigate(`/${pathLang}/islam-definition?card=${cardId}`)
                        } else if (card.ctaLink) {
                          const link = card.ctaLink
                          if (link && link.startsWith('http')) {
                            window.open(link, '_blank')
                          } else if (link && link !== '#') {
                            const internalPath = link.startsWith('/') ? link : `/${link}`
                            navigate(`/${pathLang}${internalPath}`)
                          }
                        }
                      }}
                    >
                      {getLocalizedValue(card.ctaText, currentLang) || t('home.readArticle', 'قراءة المقال')}
                    </Button>

                    {card.audioUrl && (
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => handlePlayPause(card.id, card.audioUrl)}
                          className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors flex-shrink-0"
                        >
                          {isPlaying ? (
                            <Pause className="w-4 h-4" />
                          ) : (
                            <Play className="w-4 h-4 fill-current" />
                          )}
                        </button>

                        <div className="flex items-center gap-2 bg-white dark:bg-gray-700 rounded-xl px-3 py-2 shadow-sm flex-1 sm:flex-initial min-w-0">
                          <div className="flex items-end gap-[2px] h-4 flex-shrink-0">
                            {[6, 10, 14, 9, 16, 12, 8, 13, 7].map((h, idx) => (
                              <span
                                key={idx}
                                className={`w-[2px] rounded-full transition-all ${
                                  isPlaying ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300 dark:bg-slate-500'
                                }`}
                                style={{ height: `${h}px` }}
                              />
                            ))}
                          </div>

                          <span className="text-xs font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap">
                            {formatDuration(card.audioDurationSeconds)}
                          </span>

                          <Volume2 className="w-4 h-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      </div>
    </section>
  )
}
